import React, { useCallback, useState } from "react";
import { Search } from "lucide-react";
import { useDebounce } from "use-debounce";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import useAppContext from "../../../../../context/useAppContext";
import { User, UserType, HTTPMethod, PendingInvite } from "../../../../../../models/Entities";
import PaginationFooter from "../../../../../shared/PaginationFooter";
import {
  ORGANIZATION_MEMBERS_PATH,
  ORGANIZATION_PENDING_PATH,
} from "../../../../../../router/RouterUtils";
import { usePaginatedRequest } from "../../../../../shared/hooks/usePaginatedRequest";
import {
  Card,
  Tabs,
  TabsList,
  TabPanel,
  Tab,
  Badge,
  Button,
  ButtonVariant,
  TextField,
  TextFieldVariant,
} from "@merge-api/merge-javascript-shared";
import PendingInvitesTable from "./components/PendingInvitesTable";
import MembersTable from "./components/MembersTable";
import InviteTeamMemberModal from "../../modals/InviteTeamMemberModal";
import { fetchCurrentUser, fetchWithAuth } from "../../../../../../api-client/APIClient";
import { showErrorToast, showSuccessToast } from "../../../../../shared/Toasts";

/**
 * Values must stay numeric and ordered to match the TabPanels below: the shared TabPanel takes no
 * `value` prop, so MUI Base matches panels to tabs by index. The URL is still the source of truth
 * for which tab is open, via TAB_PATHS.
 */
export enum OrganizationTab {
  MEMBERS,
  PENDING,
}

const TAB_PATHS: Record<OrganizationTab, string> = {
  [OrganizationTab.MEMBERS]: ORGANIZATION_MEMBERS_PATH,
  [OrganizationTab.PENDING]: ORGANIZATION_PENDING_PATH,
};

const OrganizationMembersCard = () => {
  // HOOKS
  const { user: meUser, setUser } = useAppContext();

  // STATE
  const history = useHistory();
  const { pathname } = useLocation();
  const [showInviteTeamMemberModal, setShowInviteTeamMemberModal] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 200);

  // the URL is the source of truth for the open tab; bare /organization falls back to Members.
  // matchPath rather than a string compare so a trailing slash resolves the same way the route does.
  const tab = matchPath(pathname, { path: ORGANIZATION_PENDING_PATH, exact: true })
    ? OrganizationTab.PENDING
    : OrganizationTab.MEMBERS;

  const membersParamsPath = debouncedSearchQuery
    ? `name__icontains=${encodeURIComponent(debouncedSearchQuery)}`
    : "";

  // HANDLERS

  const handleInviteTeamMember = (data: { email: string; user_type: UserType }) => {
    fetchWithAuth({
      path: "/organizations/users/invite",
      method: HTTPMethod.POST,
      body: data,
      onResponse: () => {
        showSuccessToast(`Successfully invited ${data.email}!`);
        refreshPendingInvites();
        setShowInviteTeamMemberModal(false);
      },
      onError: () => {
        showErrorToast(`Failed to invite ${data.email}.`);
      },
    });
  };

  // EFFECTS
  const {
    results: pendingInvites,
    refetch: refetchPendingUsers,
    onPrevious: onPreviousPendingInvites,
    onNext: onNextPendingInvites,
    hasPrevious: hasPreviousPendingInvites,
    hasNext: hasNextPendingInvites,
    isLoading: isLoadingPendingInvites,
  } = usePaginatedRequest<PendingInvite>({
    rootPath: "/organizations/users/invites",
    // the list is only rendered on the Pending tab, so don't fetch it until that tab is opened
    skip: tab !== OrganizationTab.PENDING,
  });

  const {
    results: users,
    onPrevious,
    onNext,
    onReplace: onReplaceUser,
    hasPrevious,
    hasNext,
    isLoading,
  } = usePaginatedRequest<User>({
    rootPath: "/organizations/users",
    paramsPath: membersParamsPath,
  });

  // The pending invite count comes from the me-user payload, so refresh it alongside the list
  const refreshPendingInvites = useCallback(() => {
    refetchPendingUsers();
    fetchCurrentUser(setUser);
  }, [refetchPendingUsers, setUser]);

  // DERIVED STATE
  // The footer sits below the Card and serves whichever tab is open, so it pages that tab's list
  const pagination =
    tab === OrganizationTab.PENDING
      ? {
          hasPrevious: hasPreviousPendingInvites,
          hasNext: hasNextPendingInvites,
          onPrevious: onPreviousPendingInvites,
          onNext: onNextPendingInvites,
        }
      : { hasPrevious, hasNext, onPrevious, onNext };

  return (
    <>
      <Card>
        <Tabs
          variant="underline"
          value={tab}
          onChange={(_, nextTab) => history.push(TAB_PATHS[nextTab as OrganizationTab])}
        >
          <div className="flex flex-row justify-between items-center">
            {/* tabs */}
            <TabsList className="mx-6 mt-6 mb-3">
              <Tab value={OrganizationTab.MEMBERS}>
                Members
                <Badge className="ml-1.5">{meUser.organization.users_count}</Badge>
              </Tab>
              <Tab value={OrganizationTab.PENDING}>
                Pending
                <Badge className="ml-1.5">{meUser.organization.pending_invites_count ?? 0}</Badge>
              </Tab>
            </TabsList>
            {meUser.type === UserType.admin_with_billing && (
              <Button
                className="mr-6 mt-6"
                size="sm"
                onClick={() => setShowInviteTeamMemberModal(true)}
                variant={ButtonVariant.TertiaryWhite}
              >
                Invite team member
              </Button>
            )}

            <InviteTeamMemberModal
              isShown={showInviteTeamMemberModal}
              onHide={() => setShowInviteTeamMemberModal(false)}
              onHandleSubmit={handleInviteTeamMember}
            />
          </div>

          {/* members */}
          <TabPanel>
            <div className="px-6 py-3">
              <TextField
                placeholder="Search by name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                variant={TextFieldVariant.Bordered}
                prefix={<Search size={16} className="mx-3 text-gray-40" />}
              />
            </div>
            <MembersTable users={users} isLoading={isLoading} onUserChange={onReplaceUser} />
          </TabPanel>

          {/* pending invites */}
          <TabPanel>
            <PendingInvitesTable
              pendingInvites={pendingInvites}
              isLoading={isLoadingPendingInvites || pendingInvites === undefined}
              onRefetchPendingUsers={refreshPendingInvites}
            />
          </TabPanel>
        </Tabs>
      </Card>

      <PaginationFooter
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasNext}
        onNextClick={pagination.onNext}
        onPreviousClick={pagination.onPrevious}
      />
    </>
  );
};

export default OrganizationMembersCard;
